// Shared utilities and helpers for cms_renderer

import React from "react";
import ReactMarkdown from 'react-markdown';
import cssStyles from "./cms_renderer.module.scss";
import { trackItem } from "@/utils";
import { UICompType, ComponentRenderProps } from "./index";

// For unique keys in transformTextToHtml
let CMS_ID_COUNTER = 0;

export function transformDescription(
    items: string[] | string | undefined,
    panelClassName?: string,
    trackId?: any,
    style?: React.CSSProperties,
    elemType?: string,
    props?: ComponentRenderProps,
    deviceType?: string
): React.ReactNode {
    if (Array.isArray(items)) {
        return items.map((descItem: string, index: number) => (
            transformTextToHtml(descItem, panelClassName, trackId + index, style, elemType, props, deviceType)
        ));
    } else if (typeof items === 'string') {
        return transformTextToHtml(items, panelClassName, trackId, style, elemType, props, deviceType);
    } else {
        console.error('Invalid items type:', typeof items);
        return null;
    }
}

export function transformMarkdown(text: string, classNameStr: string, elemTypeKey: keyof JSX.IntrinsicElements, id?: string, style?: React.CSSProperties, aStyle?: React.CSSProperties, componentProps?: ComponentRenderProps) {

    return (
        <ReactMarkdown components={{
            p: ({ node, ...props }) => {
                return React.createElement(
                    elemTypeKey,
                    { id, className: cssStyles[classNameStr] || classNameStr, style: { ...style, margin: '2px' } },
                    props.children
                );
            },
            a: ({ node, ...props }) => <a key={id + "" || 'link'}
                className={cssStyles[classNameStr] || classNameStr}
                id={id}
                style={{ color: 'black', textDecoration: 'none', ...aStyle }}
                {...props} />
        }}>{text}</ReactMarkdown>
    );
}

export const transformTextToHtml = (
    text: string,
    panelClassName?: string,
    id?: string,
    style?: React.CSSProperties,
    elemType: string = 'p',
    props?: ComponentRenderProps,
    deviceType?: string
) => {
    if (!text) {
        return null;
    }

    if (text === 'ELINE') {
        return <div key={id || CMS_ID_COUNTER++} style={{ height: '1em' }}>&nbsp;</div>;
    }
    const spaceMatch = text.match(/^\(SPACE=({.*?})\)$/i);
    if (spaceMatch) {
        try {
            const styleObject = JSON.parse(spaceMatch[1].replace(/'/g, '"'));
            return <div key={id || CMS_ID_COUNTER++} style={styleObject} />;
        } catch (e) {
            console.error("Error parsing SPACE style: ", e);
        }
    }

    const lineMatch = text.match(/^\(LINE=({.*?})\)$/i);
    if (lineMatch) {
        try {
            const styleObject = JSON.parse(lineMatch[1].replace(/'/g, '"'));
            return <hr key={id || CMS_ID_COUNTER++} style={styleObject} />;
        } catch (e) {
            console.error("Error parsing LINE style: ", e);
        }
    }

    if (!panelClassName) {
        panelClassName = "";
    }

    let elemTypeKey: keyof JSX.IntrinsicElements = elemType as keyof JSX.IntrinsicElements;

    try {
        let styleMatch = text.match(/\(STYLE=({.*?})\)(.*?)\(\/STYLE\)/i);
        if (styleMatch) {
            let jsonObj = JSON.parse(styleMatch[1]?.replace(/'/g, '"') || '{}');
            if (jsonObj['class']) {
                const classNameValue = jsonObj['class'];
                delete jsonObj['class'];
                const extractedStyles = extractKeyStyles(classNameValue.split('_'));
                jsonObj = { ...jsonObj, ...extractedStyles };
            }
            let styleObject: { aStyles?: React.CSSProperties, mStyles?: React.CSSProperties } = jsonObj;
            const aStyleObject: React.CSSProperties = styleObject?.aStyles || {};
            let mStyleObject: React.CSSProperties = deviceType === "Mobile" && styleObject?.mStyles ? styleObject?.mStyles : {};

            const aId = (jsonObj as any).aId || id;
            return transformMarkdown(styleMatch[2], panelClassName, elemTypeKey, aId, { ...style, ...styleObject, ...mStyleObject }, aStyleObject, props);
        }

        styleMatch = text.match(/<STYLE=({.*?})>(.*?)<\/STYLE>/i);
        if (styleMatch) {
            let styleObject: { aStyles?: React.CSSProperties, class?: string } = JSON.parse(styleMatch[1]?.replace(/'/g, '"') || '{}');
            const aStyleObject = (styleObject as { aStyles?: React.CSSProperties })?.aStyles || {};
            if (styleObject['class']) {
                const classNameValue = styleObject['class'];
                delete styleObject['class'];
                const extractedStyles = extractKeyStyles(classNameValue.split('_'));
                styleObject = { ...styleObject, ...extractedStyles };
            }
            return transformMarkdown(styleMatch[2], panelClassName, elemTypeKey, id, { ...style, ...styleObject }, aStyleObject, props);
        }

        let classMatch = text.match(/\(CLASS='(.*?)'\)(.*?)\(\/CLASS\)/i);
        if (classMatch) {
            const className = classMatch[1];
            const extractedStyles = extractKeyStyles(className.split('_'));
            return transformMarkdown(classMatch[2], className, elemTypeKey, id, { ...style, ...extractedStyles }, undefined, props);
        }

        if (text.match(/^<SMALL>/i)) {
            return transformMarkdown(text.replace(/<SMALL>/i, '').replace(/<\/SMALL>/i, ''), panelClassName + "SmallText", elemTypeKey, id, style, undefined, props)
        } else if (text.match(/^<LARGE>/i)) {
            return transformMarkdown(text.replace(/<LARGE>/i, '').replace(/<\/LARGE>/i, ''), panelClassName + "LargeText", elemTypeKey, id, style, undefined, props)
        } else if (text.match(/^<CENTER>/i)) {
            return transformMarkdown(text.replace(/<CENTER>/i, '').replace(/<\/CENTER>/i, ''), panelClassName + "CenterText", elemTypeKey, id, { ...style, textAlign: 'center' }, undefined, props);
        } else if (text.match(/^<BOLD>/i)) {
            return transformMarkdown(text.replace(/<BOLD>/i, '').replace(/<\/BOLD>/i, ''), panelClassName + "BoldText", elemTypeKey, id, { ...style, fontWeight: 'bold' }, undefined, props);
        } else if (text.match(/^ELINE$/i) || text.trim() === "") {
            return <div key={id}>&nbsp;</div>;
        } else {
            return transformMarkdown(text, panelClassName, elemTypeKey, id, style, undefined, props);
        }
    } catch (error) {
        throw new Error(`Failed to parse style object. Original error: ${error}. Input text: ${text}`);
    }
}

export function extractStylesFromUrl(urlStr: string) {
    try {
        const url = new URL(urlStr);
        const params = new URLSearchParams(url?.hash?.substring(1));
        const stylesFromUrl = Object.fromEntries(params.entries());
        const quality = stylesFromUrl.quality ? parseInt(stylesFromUrl.quality, 10) : 75;
        if ('quality' in stylesFromUrl) {
            delete stylesFromUrl.quality;
        }
        const preload = stylesFromUrl.preload === 'true';
        if ('preload' in stylesFromUrl) {
            delete stylesFromUrl.preload;
        }
        const unoptimized = stylesFromUrl.unoptimized === 'true';
        if ('unoptimized' in stylesFromUrl) {
            delete stylesFromUrl.unoptimized;
        }
        url.hash = '';
        return { 'urlStr': url.toString(), 'stylesFromUrl': stylesFromUrl, 'quality': quality, 'preload': preload, 'unoptimized': unoptimized };
    } catch (error) {
        console.info("invalid URL:", error);
        return { 'urlStr': urlStr, 'stylesFromUrl': {} };
    }
}

export const parseDimension = (dimension?: string | number | undefined): number | undefined => {
    if (typeof dimension === 'string') {
        return parseInt(dimension.replace('px', ''), 10);
    }
    return dimension;
};

export const handleProductSelection = (event: any, card?: any, sectionName?: string, cardIndex?: any,) => {
    console.log('Selected cardIndex: ' + cardIndex + "cardId : " + JSON.stringify(card) + "sectionName : " + sectionName);
    trackItem({
        id: card?.id,
        event: sectionName + 'Click',
        name: card?.name,
        section: sectionName
    });
}

export function extractKeyStyles(styleParts: string[]): React.CSSProperties {
    const keyStyles = styleParts.reduce((acc, curr) => {
        if (curr.match(/^(left|center|right|justify|start|end)$/i)) {
            acc.textAlign = curr as React.CSSProperties['textAlign'];
        } else if (curr.match(/^(small|medium|large|\d+px|\d+em|\d+rem)$/i)) {
            acc.fontSize = curr;
        } else if (curr.match(/^(#(?:[0-9a-fA-F]{3}){1,2}|red|blue|green|black|white)$/i)) {
            acc.color = curr;
        } else if (curr.match(/^(bold|normal|light|\d{3})$/i)) {
            acc.fontWeight = curr as React.CSSProperties['fontWeight'];
        } else if (curr.match(/^(italic|oblique)$/i)) {
            acc.fontStyle = curr as React.CSSProperties['fontStyle'];
        } else if (curr.match(/^(underline|line-through|none)$/i)) {
            acc.textDecoration = curr as React.CSSProperties['textDecoration'];
        } else if (curr.match(/^(Lora|Nunito)$/i)) {
            acc.fontFamily = curr;
        } else if (curr.match(/^h(\d+)(px|em|rem|%)?$/i)) {
            acc.height = curr.replace(/^h/, '');
        } else if (curr.match(/^w(\d+)(px|em|rem|%)?$/i)) {
            acc.width = curr.replace(/^w/, '');
        } else if (curr.match(/^p(\d+)(px|em|rem|%)?$/i)) {
            acc.padding = curr.replace(/^p/, '');
        } else if (curr.match(/^m(\d+)(px|em|rem|%)?$/i)) {
            acc.margin = curr.replace(/^m/, '');
        } else if (curr.match(/^pt(\d+)(px|em|rem|%)?$/i)) {
            acc.paddingTop = curr.replace(/^pt/, '');
        } else if (curr.match(/^pb(\d+)(px|em|rem|%)?$/i)) {
            acc.paddingBottom = curr.replace(/^pb/, '');
        } else if (curr.match(/^pl(\d+)(px|em|rem|%)?$/i)) {
            acc.paddingLeft = curr.replace(/^pl/, '');
        } else if (curr.match(/^pr(\d+)(px|em|rem|%)?$/i)) {
            acc.paddingRight = curr.replace(/^pr/, '');
        } else if (curr.match(/^mt(\d+)(px|em|rem|%)?$/i)) {
            acc.marginTop = curr.replace(/^mt/, '');
        } else if (curr.match(/^mb(\d+)(px|em|rem|%)?$/i)) {
            acc.marginBottom = curr.replace(/^mb/, '');
        } else if (curr.match(/^ml(\d+)(px|em|rem|%)?$/i)) {
            acc.marginLeft = curr.replace(/^ml/, '');
        } else if (curr.match(/^mr(\d+)(px|em|rem|%)?$/i)) {
            acc.marginRight = curr.replace(/^mr/, '');
        } else if (curr.match(/^b(\d+)(px|em|rem|%)?$/i)) {
            acc.border = curr.replace(/^b/, '');
        } else if (curr.match(/^bt(\d+)(px|em|rem|%)?$/i)) {
            acc.borderTop = curr.replace(/^bt/, '');
        } else if (curr.match(/^bb(\d+)(px|em|rem|%)?$/i)) {
            acc.borderBottom = curr.replace(/^bb/, '');
        } else if (curr.match(/^bl(\d+)(px|em|rem|%)?$/i)) {
            acc.borderLeft = curr.replace(/^bl/, '');
        } else if (curr.match(/^br(\d+)(px|em|rem|%)?$/i)) {
            acc.borderRight = curr.replace(/^br/, '');
        }
        return acc;
    }, {} as React.CSSProperties);
    return keyStyles;
}

// Export all types, helpers, and shared functions here.
// Example:
//export * from './types'; // If you want to split types into a types.ts file, or just define them here.